import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { PaginationProps } from 'antd';
import { useFormContext } from 'react-hook-form';
import { ApiError } from '../common/ApiClient';
import { useAdditionalFormContext } from '../common/form/AdditionalFormContext';
import { useInsertFormRow } from '../common/form/table/tanstack/useInsertFormTableRow';
import { useEditSessionContext } from './EditSessionContext';
import { type CommitPreparationResult, DraftableRow } from './useEditSession';
import { useSingleFlight } from './useSingleFlight';
import { isUnvalidated } from './utils';

interface EditSessionFormRowsContextValue {
  unvalidatedRowIndex: number | null;
  setUnvalidatedRowIndex: (value: number | null) => void;
  isPreparingPendingRow: boolean;
  preparePendingRow: () => Promise<CommitPreparationResult>;
  addRow: () => void;
  removeRow: (row: DraftableRow, index: number) => void;
  removeRows?: (rows: [DraftableRow, number][]) => void;
  /** Opens the last page (new rows are listed there) and resolves when its rows are in the form. */
  goToLastPage: () => Promise<void>;
  /** Number of rows on the pages before the current one. */
  getRowOffset: () => number;
}

const EditSessionFormRowsContext =
  createContext<EditSessionFormRowsContextValue>(undefined!);

export const useEditSessionFormRowsContext = () =>
  useContext(EditSessionFormRowsContext);

export interface EditSessionFormRowsContextProps extends PropsWithChildren {
  fieldArrayName: string;
  defaultRow: Record<string, unknown>;
  onValidationErrors: (errors: ApiError[]) => void;
  onValidationErrorsResolved: () => void;
  onOperationError: (error: unknown) => void;
  pagination?: PaginationProps;
  /** Data loaded into the form; a new value means the requested page has loaded. */
  pageData?: unknown;
}

export const EditSessionFormRowsContextProvider: FC<
  EditSessionFormRowsContextProps
> = (props) => {
  const {
    fieldArrayName,
    defaultRow,
    onValidationErrors,
    onValidationErrorsResolved,
    onOperationError,
    pagination,
    pageData,
    children,
  } = props;
  const { getValues } = useFormContext();
  const { getFieldArray } = useAdditionalFormContext();
  const insertFormRow = useInsertFormRow(fieldArrayName, true);
  const {
    deleteRow,
    deleteRows,
    validateRow,
    insertRow,
    updateRow,
    registerCommitPreparation,
  } = useEditSessionContext();

  const [unvalidatedRowIndex, setUnvalidatedRowIndex] = useState<number | null>(
    null,
  );
  const [isPreparingPendingRow, setIsPreparingPendingRow] = useState(false);
  const runPendingRowPreparation = useSingleFlight<CommitPreparationResult>();

  const addRow = useCallback(() => {
    const index = (getValues(fieldArrayName) ?? []).length;
    insertFormRow(index, { ...defaultRow });
    setUnvalidatedRowIndex(index);
  }, [insertFormRow, getValues, fieldArrayName, defaultRow]);

  const removeRow = useCallback(
    (row: DraftableRow, index: number) => {
      if (isUnvalidated(row)) {
        getFieldArray(fieldArrayName)?.remove(index);
        return;
      }
      deleteRow(row);
    },
    [getFieldArray, deleteRow, fieldArrayName],
  );

  const removeRows = useCallback(
    (rows: [DraftableRow, number][]) => {
      const deletedFromForm: number[] = [];
      const deleted: DraftableRow[] = [];
      for (const [row, index] of rows) {
        if (isUnvalidated(row)) {
          deletedFromForm.push(index);
        } else {
          deleted.push(row);
        }
      }
      if (deletedFromForm.length) {
        getFieldArray(fieldArrayName)?.remove(deletedFromForm);
      }
      if (deleted.length) {
        deleteRows(deleted);
      }
    },
    [getFieldArray, deleteRows, fieldArrayName],
  );

  const preparePendingRow = useCallback(() => {
    if (unvalidatedRowIndex === null) {
      // No pending row to prepare, return 'ready' immediately.
      return Promise.resolve<CommitPreparationResult>('ready');
    }

    return runPendingRowPreparation(async () => {
      setIsPreparingPendingRow(true);
      try {
        const rowPath = `${fieldArrayName}.${unvalidatedRowIndex}`;
        const rowData = getValues(rowPath);
        const sentRow = { ...rowData };

        if (!rowData || !isUnvalidated(rowData)) {
          setUnvalidatedRowIndex(null);
          return 'ready';
        }

        const errors = await validateRow(rowData);
        onValidationErrorsResolved();

        if (errors.length) {
          // Validation failed, return 'blocked' and notify the parent component.
          onValidationErrors(errors);
          return 'blocked';
        }

        // Validation succeeded, insert the row and return 'ready'.
        const id = await insertRow(rowData);
        const currentRow = getValues(rowPath);
        if (currentRow && isUnvalidated(currentRow)) {
          const insertedRow = { ...currentRow, id };
          getFieldArray(fieldArrayName)?.update(
            unvalidatedRowIndex,
            insertedRow,
          );
          // Send edits made while the row was being validated and inserted.
          if (
            Object.keys(currentRow).some(
              (key) => currentRow[key] !== sentRow[key],
            )
          ) {
            updateRow(insertedRow);
          }
        }
        setUnvalidatedRowIndex(null);
        return 'ready';
      } catch (error) {
        onOperationError(error);
        return 'blocked';
      } finally {
        setIsPreparingPendingRow(false);
      }
    });
  }, [
    fieldArrayName,
    getValues,
    getFieldArray,
    insertRow,
    updateRow,
    onValidationErrors,
    onValidationErrorsResolved,
    onOperationError,
    runPendingRowPreparation,
    unvalidatedRowIndex,
    validateRow,
  ]);

  useEffect(
    () => registerCommitPreparation(preparePendingRow),
    [preparePendingRow, registerCommitPreparation],
  );

  useEffect(() => {
    if (unvalidatedRowIndex === null) {
      return;
    }

    const tableContent = document
      .getElementById(fieldArrayName)
      ?.querySelector('tbody');
    const focusableTr =
      tableContent?.getElementsByTagName('tr')[unvalidatedRowIndex];

    if (!focusableTr) {
      return;
    }

    const listener = (event: FocusEvent) => {
      const { target, relatedTarget } = event;

      // Do not trigger validation if the focus stays within the row.
      if (
        relatedTarget instanceof Node &&
        focusableTr.contains(relatedTarget)
      ) {
        return;
      }

      const isInputPreview =
        target instanceof HTMLDivElement &&
        target.classList.contains('input-preview');

      // Do not trigger validation if `.input-preview` blurs with `relatedTarget: null`.
      // This happens when the real input element is not yet rendered in place of the preview element.
      if (isInputPreview) {
        return;
      }

      void preparePendingRow();
    };

    focusableTr.addEventListener('focusout', listener);
    return () => {
      focusableTr.removeEventListener('focusout', listener);
    };
  }, [fieldArrayName, preparePendingRow, unvalidatedRowIndex]);

  const paginationRef = useRef(pagination);
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);
  const resolvePageLoadRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    const resolve = resolvePageLoadRef.current;
    resolvePageLoadRef.current = null;
    // Let the form sync the new page's rows first.
    if (resolve) setTimeout(resolve);
  }, [pageData]);

  const goToLastPage = useCallback(() => {
    const {
      current = 1,
      pageSize,
      total = 0,
      onChange,
    } = paginationRef.current ?? {};
    const lastPage = pageSize ? Math.max(1, Math.ceil(total / pageSize)) : 1;
    if (!onChange || !pageSize || current >= lastPage) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      resolvePageLoadRef.current = resolve;
      onChange(lastPage, pageSize);
    });
  }, []);

  const getRowOffset = useCallback(() => {
    const { current = 1, pageSize = 0 } = paginationRef.current ?? {};
    return (current - 1) * pageSize;
  }, []);

  const value = useMemo(
    () => ({
      addRow,
      goToLastPage,
      getRowOffset,
      removeRow,
      removeRows,
      unvalidatedRowIndex,
      setUnvalidatedRowIndex,
      isPreparingPendingRow,
      preparePendingRow,
    }),
    [
      addRow,
      goToLastPage,
      getRowOffset,
      removeRow,
      removeRows,
      unvalidatedRowIndex,
      isPreparingPendingRow,
      preparePendingRow,
    ],
  );

  return (
    <EditSessionFormRowsContext.Provider value={value}>
      {children}
    </EditSessionFormRowsContext.Provider>
  );
};
